// One parameterised poll of a fund's live synthetic-NAV endpoint (5s
// default), replacing the four copy-pasted useGold/Gge/Charteris/Japan
// LiveSynthNav composables (option C, 2026-10-11 — same behaviour, one body).
//
// Fetch pattern: deliberately NOT gated behind onMounted/onUnmounted — the
// composable fetches eagerly on call (same convention as useStrikeAnchor),
// so a bare call in a unit test (no component instance) still triggers the
// load. Polling cleanup uses onScopeDispose inside an active effect scope
// (a component's setup()); outside one it's a documented no-op — the
// interval simply outlives the test. A fetcher resolving null (404: Redis
// key not yet published, early boot) yields data=null, not an error.
import { ref, getCurrentScope, onScopeDispose, type Ref } from 'vue'
import {
  api, fetchGoldSynthNavLive, fetchGgeSynthNavLive, fetchCharterisSynthNavLive, fetchAmatiSynthNavLive,
  type GoldSynthNavLive, type GgeSynthNavLive, type LiveSynthNav, type LiveSynthFund, type JapanSynthNavLive,
} from '../api/client'

export interface LiveSynthNavHandle<T> {
  data: Ref<T | null>
  loading: Ref<boolean>
  error: Ref<Error | null>
  refresh: () => Promise<void>
}

export function useLiveSynthNav<T>(fetcher: () => Promise<T | null>, pollMs = 5000): LiveSynthNavHandle<T> {
  const data = ref<T | null>(null) as Ref<T | null>
  const loading = ref(false)
  const error = ref<Error | null>(null)

  const refresh = async () => {
    loading.value = true
    try {
      data.value = await fetcher()
      error.value = null
    } catch (e) {
      error.value = e as Error
    } finally {
      loading.value = false
    }
  }

  void refresh()
  const timer = setInterval(() => { void refresh() }, pollMs)
  if (getCurrentScope()) onScopeDispose(() => clearInterval(timer))

  return { data, loading, error, refresh }
}

// Per-fund bindings. Fetchers are referenced lazily (inside the arrow) so a
// test that mocks '../api/client' with only one fund's fetcher still loads.
/** /api/gold/synth-nav-live */
export const useGoldLiveSynthNav = (pollMs = 5000): LiveSynthNavHandle<GoldSynthNavLive> =>
  useLiveSynthNav(() => fetchGoldSynthNavLive(), pollMs)
/** /api/gge/synth-nav-live */
export const useGgeLiveSynthNav = (pollMs = 5000): LiveSynthNavHandle<GgeSynthNavLive> =>
  useLiveSynthNav(() => fetchGgeSynthNavLive(), pollMs)
/** /api/{fund}/synth-nav-live — the fund-generic live K_t publisher (CHARTERIS, AMATI; Redis TTL 30s). */
export function useFundLiveSynthNav(fund: LiveSynthFund, pollMs = 5000): LiveSynthNavHandle<LiveSynthNav> {
  return useLiveSynthNav(() => (fund === 'amati' ? fetchAmatiSynthNavLive() : fetchCharterisSynthNavLive()), pollMs)
}
/** /api/charteris/synth-nav-live */
export const useCharterisLiveSynthNav = (pollMs = 5000): LiveSynthNavHandle<LiveSynthNav> =>
  useFundLiveSynthNav('charteris', pollMs)
/** /api/amati/synth-nav-live (Amati live tile, 2026-10-15) */
export const useAmatiLiveSynthNav = (pollMs = 5000): LiveSynthNavHandle<LiveSynthNav> =>
  useFundLiveSynthNav('amati', pollMs)
/** /api/japan/synth-nav-live (spec 2026-10-04) */
export const useJapanLiveSynthNav = (pollMs = 5000): LiveSynthNavHandle<JapanSynthNavLive> =>
  useLiveSynthNav(() => api.japanSynthNavLive(), pollMs)
